/* Trappbutiken – önskelista som drawer (Wubs)
 *
 * SKAL. All lagring går genom WubsWishlistStore. När App Proxy + customer metafield
 * `custom.wishlist` finns: byt ut read()/write() i den klassen mot fetch mot proxyn
 * (och merga localStorage-listan vid inloggning). Inget annat behöver röras.
 */

const WUBS_WL_KEY = 'wubs:wishlist';
const WUBS_WL_EVENT = 'wubs:wishlist:change';
const WUBS_WL_PARAM = 'wishlist';
/* Vems är den lokala kopian (2026-11-03)? Saknas nyckeln är det en gästlista. Se wubsWlBoot(). */
const WUBS_WL_AGARE = 'wubs:wishlist:agare';
/* Tiden för en ändring som servern inte bekräftat, eller 'fel' om anropet misslyckades. */
const WUBS_WL_OSYNKAD = 'wubs:wishlist:osynkad';
const WUBS_WL_PARKERAD = 'wubs:wishlist:parkerad';
const WUBS_WL_OSYNKAD_MAX = 5 * 60 * 1000;

let wubsWlCfgSeq = 0;
function wubsWlUid() {
  wubsWlCfgSeq += 1;
  return 'k' + Date.now().toString(36) + '-' + wubsWlCfgSeq.toString(36) + '-' + Math.floor(Math.random() * 1e6).toString(36);
}

const wubsWlMoney = new Intl.NumberFormat('sv-SE', {
  style: 'currency',
  currency: 'SEK',
  maximumFractionDigits: 0,
});

/* ------------------------------------------------------------------ *
 * Lagring – enda stället som vet VAR listan bor
 * ------------------------------------------------------------------ */
class WubsWishlistStore {
  /** @returns {{handle: string, variantId: number|null}[]} */
  static read() {
    try {
      const raw = JSON.parse(localStorage.getItem(WUBS_WL_KEY) || '[]');
      return Array.isArray(raw) ? raw.filter((e) => e && (e.handle || e.typ === 'konfig')) : [];
    } catch {
      return [];
    }
  }

  static write(items) {
    try {
      localStorage.setItem(WUBS_WL_KEY, JSON.stringify(items));
    } catch {
      /* privat läge – listan blir sessionsbunden, inte värt att krascha för */
    }
    document.dispatchEvent(new CustomEvent(WUBS_WL_EVENT, { detail: { items } }));
    // Inloggad kund → spegla till kundmetafältet via App Proxy (Worker). Gäst → bara localStorage.
    wubsWlSyncToServer(items);
  }

  static has(handle) {
    return WubsWishlistStore.read().some((e) => e.handle === handle);
  }

  static add(handle, variantId = null) {
    const items = WubsWishlistStore.read();
    if (items.some((e) => e.handle === handle)) return items;
    items.unshift({ handle, variantId: variantId ? Number(variantId) : null });
    WubsWishlistStore.write(items);
    return items;
  }

  static remove(handle) {
    const items = WubsWishlistStore.read().filter((e) => e.handle !== handle);
    WubsWishlistStore.write(items);
    return items;
  }

  static toggle(handle, variantId = null) {
    return WubsWishlistStore.has(handle)
      ? (WubsWishlistStore.remove(handle), false)
      : (WubsWishlistStore.add(handle, variantId), true);
  }

  static count() {
    return WubsWishlistStore.read().length;
  }

  /** Sparad konfiguration (t.ex. ledstång) – ett konfig-objekt, ingen produkt-handle.
   *  Samma konfigText = samma post, så en dubbelklick inte ger två rader.
   *  🧨 Texten bär inte beslag, tillbehör eller urtagets millimeter (2026-11-03). Sparades en ändrad
   *  konfiguration stod det "Sparad ✓" medan den gamla posten låg kvar, med gamla val och gammal länk.
   *  Nu ersätts posten på samma plats och behåller sitt id. */
  static addConfig(entry) {
    const items = WubsWishlistStore.read();
    const e = Object.assign({ typ: 'konfig' }, entry);
    const i = e.konfigText
      ? items.findIndex((x) => x.typ === 'konfig' && x.sort === e.sort && x.konfigText === e.konfigText)
      : -1;
    if (i > -1) {
      e.id = items[i].id || e.id || wubsWlUid();
      items[i] = e;
    } else {
      if (!e.id) e.id = wubsWlUid();
      items.unshift(e);
    }
    WubsWishlistStore.write(items);
    return items;
  }

  static removeConfig(id) {
    const items = WubsWishlistStore.read().filter((e) => !(e.typ === 'konfig' && e.id === id));
    WubsWishlistStore.write(items);
    return items;
  }

  /** Slår ihop en delad lista med den egna (används av ?wishlist=-importen) */
  static merge(incoming) {
    const items = WubsWishlistStore.read();
    const known = new Set(items.map((e) => e.handle));
    let added = 0;
    for (const e of incoming) {
      if (e.handle && !known.has(e.handle)) {
        items.push(e);
        known.add(e.handle);
        added += 1;
      }
    }
    if (added) WubsWishlistStore.write(items);
    return added;
  }
}

/* Publikt API så andra sektioner (t.ex. ledstångskonfiguratorn) kan spara utan
 * att känna till lagringen. window.* eftersom top-level const inte når andra
 * classic scripts tillförlitligt. */
window.WubsWishlist = {
  addConfig: (entry) => WubsWishlistStore.addConfig(entry),
  add: (handle, variantId) => WubsWishlistStore.add(handle, variantId),
  has: (handle) => WubsWishlistStore.has(handle),
  read: () => WubsWishlistStore.read(),
  open: () => document.querySelector('wubs-wishlist-drawer')?.open?.(),
};

/* ------------------------------------------------------------------ *
 * Server-synk – kundmetafält via App Proxy (Cloudflare Worker, wishlist-worker/).
 * Inloggad kund → spegla listan till custom.wishlist. Gäst → bara localStorage.
 * Faller tyst tillbaka på localStorage om proxyn inte är deployad ännu.
 * ------------------------------------------------------------------ */
const WUBS_WL_ENDPOINT = '/apps/wishlist';
let WUBS_WL_LOGGED_IN = false;
let wubsWlSyncTimer = null;

/* Temat sparar `variantId`, Workern har sparat `variant` (som text). En inloggad kund tappade därför sin
   variant vid omladdning (2026-11-03). Läs alltid varianten härifrån. */
function wubsWlVariantId(e) {
  const v = e?.variantId ?? e?.variant;
  return v ? Number(v) || null : null;
}

const WUBS_WL_KONFIG_START = { ledstang: '/pages/ledstang-konfig', troskel: '/pages/troskel-konfig', hyllplan: '/pages/hyllplan-konfig' };

/* Länken till konfiguratorn på en sparad post, annars ''. Bara sökväg + fråga behålls: listan kan komma
   från en delad länk (så ingen javascript: eller annan sajt), och posten kan vara sparad på en annan domän. */
function wubsWlKonfigLank(e) {
  if (!e?.url) return '';
  try {
    const u = new URL(e.url, window.location.origin);
    return /^https?:$/.test(u.protocol) ? window.location.origin + u.pathname + u.search : '';
  } catch {
    return '';
  }
}

function wubsWlItemKey(e) {
  return e && e.typ === 'konfig'
    ? 'k:' + (e.id || e.konfigText || e.etikett || JSON.stringify(e.lines || {}))
    : 'p:' + (e && e.handle);
}
function wubsWlUnion(a, b) {
  const out = Array.isArray(a) ? a.slice() : [];
  const seen = new Set(out.map(wubsWlItemKey));
  for (const e of Array.isArray(b) ? b : []) {
    const k = wubsWlItemKey(e);
    if (!seen.has(k)) { out.push(e); seen.add(k); }
  }
  return out;
}

function wubsWlLas(nyckel) {
  try { return localStorage.getItem(nyckel); } catch { return null; }
}
function wubsWlSkriv(nyckel, varde) {
  try {
    if (varde == null) localStorage.removeItem(nyckel);
    else localStorage.setItem(nyckel, varde);
  } catch { /* privat läge */ }
}

function wubsWlPosta(items) {
  const body = JSON.stringify({ items });
  /* Ett långsamt svar får inte sudda markeringen för en nyare ändring: då gav spara, ta bort, gå vidare
     en ny sida utan markering, och servern la tillbaka det borttagna. */
  const markering = wubsWlLas(WUBS_WL_OSYNKAD);
  fetch(WUBS_WL_ENDPOINT, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body,
    keepalive: body.length < 60000, // webbläsarens tak är 64 kB, över det hade anropet kastat
  })
    .then((r) => {
      if (!r.ok) wubsWlSkriv(WUBS_WL_OSYNKAD, 'fel');
      else if (wubsWlLas(WUBS_WL_OSYNKAD) === markering) wubsWlSkriv(WUBS_WL_OSYNKAD, null);
    })
    .catch(() => wubsWlSkriv(WUBS_WL_OSYNKAD, 'fel')); // offline / proxy nere → nästa sidladdning försöker igen
}

let wubsWlSyncVantar = null;
function wubsWlSyncToServer(items) {
  if (!WUBS_WL_LOGGED_IN) return;
  wubsWlSkriv(WUBS_WL_OSYNKAD, String(Date.now()));
  wubsWlSyncVantar = items;
  clearTimeout(wubsWlSyncTimer);
  wubsWlSyncTimer = setTimeout(() => {
    wubsWlSyncVantar = null;
    wubsWlPosta(items);
  }, 500);
}
/* Kunden tar bort och går vidare inom en halv sekund: timern dör med sidan, och utan det här hade
   servern, som nu bestämmer vid nästa laddning, lagt tillbaka det borttagna. */
window.addEventListener('pagehide', () => {
  if (!wubsWlSyncVantar) return;
  clearTimeout(wubsWlSyncTimer);
  wubsWlPosta(wubsWlSyncVantar);
  wubsWlSyncVantar = null;
});

/* Har den lokala kopian en ändring som servern kanske inte fått? Svaret hinner sällan komma när kunden
   går vidare, och nästa sida kan vara renderad före sparningen. Tidsgränsen: stängde kunden fliken direkt
   står markeringen kvar, och den ska inte kunna skriva över en borttagning från telefonen dagar senare. */
function wubsWlHarOsynkat() {
  const m = wubsWlLas(WUBS_WL_OSYNKAD);
  if (!m) return false;
  return m === 'fel' || Date.now() - Number(m) < WUBS_WL_OSYNKAD_MAX;
}

/* Workern sparade inte `url` på konfigurationer före 2026-11-03. Finns länken i den lokala kopian tas den
   därifrån, annars hade kunden tappat "Öppna och lägg i varukorgen". Svarar med samma objekt när inget
   ändrats, så `wubsWlLankarNya` ser om servern behöver få länkarna. */
function wubsWlBehallLankar(server, local) {
  const lankar = new Map(local.filter((e) => e.typ === 'konfig' && e.url).map((e) => [e.id, e.url]));
  return server.map((e) => (e.typ === 'konfig' && !e.url && lankar.has(e.id) ? Object.assign({}, e, { url: lankar.get(e.id) }) : e));
}
/* Annars kom länken aldrig upp: utloggning eller en annan enhet gav posten utan länk, och den köptes till sparat pris. */
function wubsWlLankarNya(medLankar, server) {
  return medLankar.some((e, i) => e !== server[i]);
}

/* En sparning servern aldrig fick (utgången session, Workern nere) ska inte försvinna vid utloggningen, men
   inte heller synas för nästa person vid datorn. Den läggs undan och kommer bara tillbaka för samma kund. */
function wubsWlParkera(agare) {
  if (wubsWlLas(WUBS_WL_OSYNKAD) !== 'fel') return;
  wubsWlSkriv(WUBS_WL_PARKERAD, JSON.stringify({ agare, items: WubsWishlistStore.read() }));
}
function wubsWlHamtaParkerad(kund) {
  const rad = wubsWlLas(WUBS_WL_PARKERAD);
  if (!rad) return null;
  wubsWlSkriv(WUBS_WL_PARKERAD, null);
  try {
    const p = JSON.parse(rad);
    return p && p.agare === kund && Array.isArray(p.items) ? p.items : null;
  } catch {
    return null;
  }
}

/* 🧨 VEMS LISTA ÄR DET? (2026-11-03) Förut slogs serverns och den lokala listan ihop vid varje laddning:
   en borttagning på telefonen kom tillbaka från datorn, och efter en utloggning på en delad dator hamnade
   första kundens poster på nästa kunds kundkort. Nu minns vi vilken kund den lokala kopian tillhör:
     utloggad + kundens kopia   → kopian rensas (en obekräftad ändring läggs undan till samma kund)
     samma kund                 → servern bestämmer, utom när den lokala har en obekräftad ändring
     gästlista (ingen ägare)    → slås ihop EN gång, sedan bestämmer servern
     en annan kunds kopia       → slängs, slås aldrig ihop
   ⚠️ En lista från före den här ändringen saknar ägare och räknas som gästlista första gången. */
function wubsWlBoot() {
  let boot = { loggedIn: false, items: [] };
  try {
    const el = document.getElementById('wubs-wl-boot');
    if (el) boot = JSON.parse(el.textContent || '{}');
  } catch { /* ignore */ }
  WUBS_WL_LOGGED_IN = !!boot.loggedIn;
  // Efter login-redirect: ta kunden tillbaka till ursprungssidan + öppna önskelistan.
  wubsAuthHandleReturn();
  const agare = wubsWlLas(WUBS_WL_AGARE);

  if (!WUBS_WL_LOGGED_IN) {
    if (agare) {
      wubsWlParkera(agare);
      wubsWlSkriv(WUBS_WL_KEY, null);
      wubsWlSkriv(WUBS_WL_AGARE, null);
      wubsWlSkriv(WUBS_WL_OSYNKAD, null);
    }
    return;
  }

  const kund = String(boot.kundId || '');
  const local = WubsWishlistStore.read();
  let server = Array.isArray(boot.items) ? boot.items : [];
  let lista = server;
  let skicka = false;
  const parkerad = wubsWlHamtaParkerad(kund);
  if (parkerad) {
    lista = wubsWlUnion(parkerad, local);
    skicka = true;
  } else if (!agare) {
    const medLankar = wubsWlBehallLankar(server, local);
    lista = wubsWlUnion(medLankar, local);
    skicka = lista.length !== server.length || wubsWlLankarNya(medLankar, server);
  } else if (agare === kund && wubsWlHarOsynkat()) {
    lista = local;
    skicka = true;
  } else if (agare === kund) {
    lista = wubsWlBehallLankar(server, local);
    skicka = wubsWlLankarNya(lista, server);
  }
  wubsWlSkriv(WUBS_WL_KEY, JSON.stringify(lista));
  wubsWlSkriv(WUBS_WL_AGARE, kund);
  if (!skicka) wubsWlSkriv(WUBS_WL_OSYNKAD, null);
  document.dispatchEvent(new CustomEvent(WUBS_WL_EVENT, { detail: { items: lista } }));
  if (skicka) wubsWlSyncToServer(lista);
}

/* ------------------------------------------------------------------ *
 * Inloggning – nya kundkonton hostar login på shopify.com. Vi använder
 * storefront-login-routen `/customer_authentication/login?return_to=<sökväg>`
 * som INloggar via Shopifys OAuth och skickar tillbaka till callbacken på
 * butiksdomänen → return_to (exakt sidan man kom från). ⚠️ `/account/login`
 * dumpar i stället kunden på shopify.com/account (profilsidan) – använd INTE den.
 * En localStorage-flagga säger åt oss att öppna önskelistan när kunden är tillbaka.
 * ------------------------------------------------------------------ */
const WUBS_WL_RET_OPEN = 'wubs:wl:openWl';
const WUBS_WL_RET_AT = 'wubs:wl:returnAt';
const WUBS_WL_RET_MAXAGE = 15 * 60 * 1000; // gammal intent slängs efter 15 min

function wubsAuthLogin() {
  if (WUBS_WL_LOGGED_IN) { window.location.href = '/account'; return; }
  try {
    localStorage.setItem(WUBS_WL_RET_OPEN, '1');
    localStorage.setItem(WUBS_WL_RET_AT, String(Date.now()));
  } catch (e) { /* ignore */ }
  // Tillbaka till EXAKT denna sida efter login (behåller ev. query, t.ex. preview_theme_id).
  const back = window.location.pathname + window.location.search;
  window.location.href = '/customer_authentication/login?return_to=' + encodeURIComponent(back);
}

function wubsAuthClearReturn() {
  try {
    localStorage.removeItem(WUBS_WL_RET_OPEN);
    localStorage.removeItem(WUBS_WL_RET_AT);
  } catch (e) { /* ignore */ }
}

function wubsAuthOpenWishlistSoon() {
  const open = () => document.querySelector('wubs-wishlist-drawer')?.open?.();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', open);
  else setTimeout(open, 60);
}

/* Körs vid varje sidladdning (från boot): är kunden tillbaka inloggad efter en
   login-intent → öppna önskelistan. return_to landar oss redan på rätt sida. */
function wubsAuthHandleReturn() {
  let openWl = null;
  let at = 0;
  try {
    openWl = localStorage.getItem(WUBS_WL_RET_OPEN);
    at = parseInt(localStorage.getItem(WUBS_WL_RET_AT) || '0', 10) || 0;
  } catch (e) { /* ignore */ }

  if (!openWl) return;
  if (at && Date.now() - at > WUBS_WL_RET_MAXAGE) { wubsAuthClearReturn(); return; }
  if (!WUBS_WL_LOGGED_IN) return; // öppna bara när kunden faktiskt är tillbaka inloggad

  wubsAuthClearReturn();
  wubsAuthOpenWishlistSoon();
}

window.WubsAuth = { login: wubsAuthLogin, isLoggedIn: () => WUBS_WL_LOGGED_IN };

/* Bakåtkompat: gamla delningslänkar `?wishlist=handle1,handle2` (bara produkt-handles).
   Nya länkar använder `?wl=<signerad token>` → server-hämtning, se share()/importShared(). */
const wubsWlDecode = (raw) =>
  decodeURIComponent(raw || '')
    .split(',')
    .map((h) => h.trim())
    .filter(Boolean)
    .map((handle) => ({ handle, variantId: null }));

/* ------------------------------------------------------------------ *
 * Drawern
 * ------------------------------------------------------------------ */
class WubsWishlistDrawer extends HTMLElement {
  connectedCallback() {
    this.body = this.querySelector('[data-wubs-wl-body]');
    this.foot = this.querySelector('[data-wubs-wl-foot]');
    this.countEl = this.querySelector('[data-wubs-wl-count]');
    this.introEl = this.querySelector('[data-wubs-wl-intro]');
    this.noteEl = this.querySelector('[data-wubs-wl-note]');
    this.emptyTpl = this.querySelector('[data-wubs-wl-empty]');
    this.itemTpl = this.querySelector('[data-wubs-wl-item]');
    this.konfigTpl = this.querySelector('[data-wubs-wl-konfig]');
    this.loginEl = this.querySelector('[data-wubs-wl-login]');
    this.cache = new Map();

    this.importFromUrl();
    this.bindTriggers();
    this.bindInside();
    this.render();
    this.syncBadge();

    document.addEventListener(WUBS_WL_EVENT, () => {
      this.render();
      this.syncBadge();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });
  }

  /** ?wl=<token> → hämta den delade listan (server) och merga. Bakåtkompat: ?wishlist=handles. */
  importFromUrl() {
    const url = new URL(window.location.href);

    const token = url.searchParams.get('wl');
    if (token) {
      url.searchParams.delete('wl');
      window.history.replaceState({}, '', url);
      this.importShared(token); // async – öppnar drawern när listan hämtats
      return;
    }

    const raw = url.searchParams.get(WUBS_WL_PARAM);
    if (!raw) return;
    const added = WubsWishlistStore.merge(wubsWlDecode(raw));
    url.searchParams.delete(WUBS_WL_PARAM);
    window.history.replaceState({}, '', url);
    if (added) this.pendingOpen = true;
  }

  /** Hämta en delad lista via signerad token och merga in (produkter + konfigurationer). */
  async importShared(token) {
    try {
      const d = await fetch(`${WUBS_WL_ENDPOINT}?wl=${encodeURIComponent(token)}`, {
        headers: { accept: 'application/json' },
      }).then((r) => (r.ok ? r.json() : null));
      const incoming = d && Array.isArray(d.items) ? d.items : [];
      if (!incoming.length) return;
      const merged = wubsWlUnion(WubsWishlistStore.read(), incoming);
      WubsWishlistStore.write(merged); // render + (om inloggad) synk
      this.open();
    } catch (e) { /* ogiltig/utgången länk eller proxy nere → tyst */ }
  }

  bindTriggers() {
    const link = document.querySelector('#wubs-wishlist-bubble');
    if (link && !link.dataset.wubsWlBound) {
      link.dataset.wubsWlBound = '1';
      link.setAttribute('role', 'button');
      link.setAttribute('aria-haspopup', 'dialog');
      link.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    }
    if (this.pendingOpen) {
      this.pendingOpen = false;
      this.open();
    }
  }

  bindInside() {
    this.addEventListener('click', async (e) => {
      if (e.target.closest('[data-wubs-wl-close]')) return this.close();

      // Logga in → redirect till Shopifys login (WubsAuth), tillbaka hit efteråt.
      if (e.target.closest('[data-wubs-wl-login-btn]')) {
        if (window.WubsAuth) window.WubsAuth.login();
        return;
      }

      // Sparad konfiguration (eget kort, ingen handle)
      const cfgRow = e.target.closest('[data-konfig-id]');
      if (e.target.closest('[data-remove-konfig]') && cfgRow) {
        return WubsWishlistStore.removeConfig(cfgRow.dataset.konfigId);
      }

      const row = e.target.closest('[data-handle]');

      if (e.target.closest('[data-remove]') && row) {
        return WubsWishlistStore.remove(row.dataset.handle);
      }

      const addBtn = e.target.closest('[data-add]');
      if (addBtn && row) {
        return this.addToCart([row.dataset.handle], addBtn);
      }

      const addAll = e.target.closest('[data-wubs-wl-addall]');
      if (addAll) {
        return this.addAllToCart(addAll);
      }

      if (e.target.closest('[data-wubs-wl-share]')) return this.share();
    });
  }

  /* --- Öppna/stäng --- */
  open() {
    this.hidden = false;
    WubsScroll.las();   // låser <html>, inte <body> – annars tappar sticky sin scrollport
    void this.offsetWidth; // synkron reflow – rAF throttlas och lämnar panelen utanför kanten
    this.classList.add('is-open');
    this.querySelector('.wubs-wl__close')?.focus();
  }

  close() {
    if (this.hidden) return;
    this.classList.remove('is-open');
    WubsScroll.slapp();
    setTimeout(() => {
      this.hidden = true;
    }, 220);
  }

  /* --- Produktdata --- */
  async product(handle) {
    if (this.cache.has(handle)) return this.cache.get(handle);
    const root = window.Shopify?.routes?.root || '/';
    try {
      const [p, kop] = await Promise.all([
        fetch(`${root}products/${handle}.js`).then((r) => (r.ok ? r.json() : null)),
        fetch(`${root}products/${handle}?view=franpris`).then((r) => (r.ok ? r.json() : null)).catch(() => null),
      ]);
      if (p) p.wubsKop = kop;
      this.cache.set(handle, p);
      return p;
    } catch {
      return null;
    }
  }

  /* 🧨 MÅTTVAROR KÖPS PÅ PRODUKTSIDAN (2026-11-03). Variantpriset är bara en taxa: tröskeln gick att lägga i
     korgen härifrån för 3,60 kr, utan mått. Samma regel som knappen på produktkortet, läst ur `?view=franpris`.
     Går uppgiften inte att läsa skickas kunden hellre till produktsidan än att en rad utan mått kan köpas. */
  kravsMatt(p, variant) {
    const k = p?.wubsKop;
    if (!k || typeof k.mattvara !== 'boolean') return true;
    if (!k.mattvara) return false;
    return !k.blandad || !!(k.varianter || []).find((x) => x.id === variant?.id)?.modell;
  }

  /* Från-priset: variantens om det finns, annars produktens. Samma tal som produktsidan visar. */
  franPris(p, variant) {
    const k = p?.wubsKop;
    if (!k) return null;
    const v = (k.varianter || []).find((x) => x.id === variant?.id);
    const f = v?.pris > 0 ? v : k.produkt;
    return f?.pris > 0 ? f : null;
  }

  valdVariant(p, saved) {
    const id = wubsWlVariantId(saved);
    return p.variants.find((v) => v.id === id) || p.variants.find((v) => v.available);
  }

  /* --- Rendering --- */
  async render() {
    const items = WubsWishlistStore.read();

    if (this.countEl) {
      this.countEl.textContent = items.length;
      this.countEl.hidden = items.length === 0;
    }
    if (this.introEl) this.introEl.hidden = items.length === 0;
    if (this.foot) this.foot.hidden = items.length === 0;
    // Gäst med sparade rader → prompta inloggning för att spara listan på kundkortet.
    if (this.loginEl) this.loginEl.hidden = WUBS_WL_LOGGED_IN || items.length === 0;

    if (!items.length) {
      this.body.innerHTML = '';
      if (this.emptyTpl) this.body.appendChild(this.emptyTpl.content.cloneNode(true));
      return;
    }

    // Kampanjindexet hämtas parallellt med produkterna, så raderna aldrig hinner visa fel pris först.
    const [products, kampanjer] = await Promise.all([
      Promise.all(items.map((i) => (i.typ === 'konfig' ? Promise.resolve(null) : this.product(i.handle)))),
      window.WubsKampanj ? window.WubsKampanj.index() : Promise.resolve({}),
    ]);
    const list = document.createElement('ul');
    list.className = 'wubs-wl__items';
    list.setAttribute('role', 'list');
    let attKopa = 0;
    let attMata = 0;
    let attKonfig = 0;

    items.forEach((item, i) => {
      if (item.typ === 'konfig') {
        const knode = this.renderKonfig(item);
        if (knode) {
          list.appendChild(knode);
          attKonfig++;
        }
        return;
      }
      const p = products[i];
      if (!p) return; // produkten finns inte kvar – hoppa över tyst
      const node = this.itemTpl.content.cloneNode(true);
      const li = node.querySelector('.wubs-wl__item');
      li.dataset.handle = item.handle;

      const variant = p.variants.find((v) => v.id === wubsWlVariantId(item)) || p.variants[0];
      const img = p.featured_image || variant?.featured_image?.src;

      const media = node.querySelector('[data-media]');
      media.href = p.url;
      const imgEl = node.querySelector('[data-img]');
      if (img) {
        imgEl.src = `${img}${img.includes('?') ? '&' : '?'}width=144`;
        imgEl.alt = p.title;
      } else {
        imgEl.remove();
      }

      const titleEl = node.querySelector('[data-title]');
      titleEl.href = p.url;
      titleEl.textContent = p.title;

      if (p.variants.length > 1 && variant) {
        const v = node.querySelector('[data-variant]');
        v.textContent = variant.title;
        v.hidden = false;
      }

      if (this.kravsMatt(p, variant)) {
        const fran = this.franPris(p, variant);
        const pris = node.querySelector('[data-price]');
        pris.textContent = fran ? `Från ${wubsWlMoney.format(fran.pris / 100)} ` : 'Pris enligt dina mått';
        if (fran?.kampanj && fran.ord > fran.pris) {
          const ordFran = document.createElement('s');
          ordFran.className = 'wubs-wl__item-ord';
          ordFran.textContent = wubsWlMoney.format(fran.ord / 100);
          pris.appendChild(ordFran);
          pris.classList.add('wubs-wl__item-price--kampanj');
        }
        const lank = document.createElement('a');
        lank.className = 'wubs-wl__item-add';
        lank.href = variant ? `${p.url}?variant=${variant.id}` : p.url;
        lank.textContent = 'Ange mått';
        node.querySelector('[data-add]').replaceWith(lank);
        attMata++;
        list.appendChild(node);
        return;
      }

      // KAMPANJPRIS – samma regel som korten. Ordinarie pris stryks över i ett eget <s>.
      const ore = variant?.price ?? p.price;
      const kampanj = window.WubsKampanj?.pris(ore, kampanjer[item.handle]);
      const prisEl = node.querySelector('[data-price]');
      if (kampanj != null) {
        prisEl.textContent = wubsWlMoney.format(kampanj / 100) + ' ';
        const ord = document.createElement('s');
        ord.className = 'wubs-wl__item-ord';
        ord.textContent = wubsWlMoney.format(ore / 100);
        prisEl.appendChild(ord);
        prisEl.classList.add('wubs-wl__item-price--kampanj');
      } else {
        prisEl.textContent = wubsWlMoney.format(ore / 100);
      }

      const add = node.querySelector('[data-add]');
      if (!variant?.available) {
        add.disabled = true;
        add.textContent = 'Tillfälligt slut';
      } else {
        attKopa++;
      }

      list.appendChild(node);
    });

    this.body.innerHTML = '';
    this.body.appendChild(list);
    this.visaKopRad(attKopa, attMata, attKonfig);
  }

  /* "Lägg allt" lägger bara det som går att köpa utan mått eller konfigurator, och det ska stå. */
  visaKopRad(attKopa, attMata, attKonfig) {
    const knapp = this.querySelector('[data-wubs-wl-addall]');
    if (knapp) knapp.hidden = attKopa === 0;
    if (!this.mattNot && knapp) {
      this.mattNot = document.createElement('p');
      this.mattNot.className = 'wubs-wl__matt-not';
      knapp.before(this.mattNot);
    }
    if (this.mattNot) {
      const delar = [];
      if (attMata > 0) delar.push('Varor med "Ange mått" följer inte med, de läggs i varukorgen på produktsidan.');
      if (attKonfig > 0) delar.push('Sparade konfigurationer följer inte med, de öppnas och läggs i varukorgen en och en.');
      this.mattNot.textContent = attKopa > 0 ? delar.join(' ') : '';
      this.mattNot.hidden = !(attKopa > 0 && delar.length > 0);
    }
  }

  /* En sparad konfiguration – eget kort (ingen produkt-handle). */
  renderKonfig(item) {
    if (!this.konfigTpl) return null;
    const node = this.konfigTpl.content.cloneNode(true);
    const li = node.querySelector('.wubs-wl__item');
    if (li) li.dataset.konfigId = item.id || '';

    const et = node.querySelector('[data-etikett]');
    if (et) et.textContent = item.etikett || 'Sparad konfiguration';

    const rows = node.querySelector('[data-rows]');
    if (rows) {
      const parts = (item.konfigText || '').split('||').map((s) => s.trim()).filter(Boolean);
      rows.innerHTML = '';
      parts.forEach((p) => {
        const r = document.createElement('li');
        r.textContent = p;
        rows.appendChild(r);
      });
      rows.hidden = parts.length === 0;
    }

    const price = node.querySelector('[data-price]');
    if (price) price.textContent = item.pris != null ? wubsWlMoney.format(item.pris) : '';

    /* 🧨 SPARAD KONFIGURATION KÖPS I KONFIGURATORN (2026-11-03). Raderna bär det sparade `_components`-priset:
       spara, priset ändras, lägg i korgen härifrån → gamla priset debiteras. Med `url` blir knappen därför en
       länk till konfiguratorn, som räknar om priset. Den länken är också "Ändra", så någon andra länk behövs inte.
       Poster som saknar `url` (2 stycken 2026-11-03, Workern tog bort länken fram till samma dag) går inte att
       återskapa: de öppnar en tom konfigurator, och texten ovanför säger vad kunden valde. */
    const lank = wubsWlKonfigLank(item);
    const start = WUBS_WL_KONFIG_START[item.sort];
    const oppna = node.querySelector('[data-open-konfig]');
    if (oppna && (lank || start)) {
      oppna.href = lank || start;
      if (!lank) oppna.textContent = 'Öppna konfiguratorn';
      oppna.hidden = false;
    } else {
      oppna?.remove();
    }

    return node;
  }

  /* --- Varukorg --- */
  async addToCart(handles, btn) {
    if (!handles.length) return;
    const products = await Promise.all(handles.map((h) => this.product(h)));
    const wanted = WubsWishlistStore.read();
    const lines = products
      .map((p) => {
        if (!p) return null;
        const saved = wanted.find((w) => w.handle === p.handle);
        const variant = this.valdVariant(p, saved);
        return variant && !this.kravsMatt(p, variant) ? { id: variant.id, quantity: 1 } : null;
      })
      .filter(Boolean);
    return this.pushToCart(lines, btn);
  }

  /* Lägg rader i varukorgen. */
  async pushToCart(lines, btn) {
    if (!lines || !lines.length) return;
    const label = btn?.textContent;
    const drawer = document.querySelector('wubs-cart-drawer');
    this.querySelectorAll('.wubs-kopfel').forEach((rad) => rad.remove()); // nytt försök, gamla besked ut
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Lägger till…';
    }
    try {
      const root = window.Shopify?.routes?.root || '/';
      if (drawer && typeof drawer.cartRequest === 'function') {
        await drawer.cartRequest('cart/add.js', { items: lines });
        this.close();
        drawer.open();
      } else {
        const res = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: lines, sections: 'wubs-cart-drawer' }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        document.querySelector('wubs-cart-drawer')?.render?.(await res.json());
        this.close();
        document.querySelector('wubs-cart-drawer')?.open?.();
      }
    } catch (fel) {
      /* Korgen sa nej eller nätet är borta (2026-11-03): listan står kvar, varukorgen öppnas inte utan varan.
         Knappen hittas via klasserna i kopFelAnkare; finns ingen låda står beskedet i listans notisrad. */
      if (drawer?.visaKopFel && btn) drawer.visaKopFel(btn);
      else this.note('Det gick inte att lägga i varukorgen. Försök igen.');
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.textContent = label;
      }
    }
  }

  /* "Lägg allt": produkter (vald/första tillgängliga variant). Sparade konfigurationer köps i
     konfiguratorn, se renderKonfig(), och raden ovanför knappen säger det. */
  async addAllToCart(btn) {
    const all = WubsWishlistStore.read();
    const productItems = all.filter((i) => i.handle);
    const products = await Promise.all(productItems.map((i) => this.product(i.handle)));
    const lines = [];
    products.forEach((p, i) => {
      if (!p) return;
      const saved = productItems[i];
      const variant = this.valdVariant(p, saved);
      if (variant && !this.kravsMatt(p, variant)) lines.push({ id: variant.id, quantity: 1 });
    });
    return this.pushToCart(lines, btn);
  }

  /* --- Delning ---
     Listan bor på kundkortet → delningslänken bär en SIGNERAD token (kundId + utgång +
     HMAC, skapad av Workern) och mottagaren hämtar listan live. Kräver inloggning för att
     dela (annars finns ingen serverlista). Full fidelity: varianter + konfigurationer. */
  async share() {
    const items = WubsWishlistStore.read();
    if (!items.length) return;

    if (!WUBS_WL_LOGGED_IN) {
      this.note('Logga in för att dela din önskelista.');
      if (window.WubsAuth) window.WubsAuth.login();
      return;
    }

    let token = null;
    try {
      // POST speglar den aktuella listan till kundkortet OCH returnerar en färsk token.
      const d = await fetch(WUBS_WL_ENDPOINT, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ items }),
      }).then((r) => (r.ok ? r.json() : null));
      token = d && d.shareToken;
    } catch (e) { /* offline / proxy nere */ }

    if (!token) {
      this.note('Kunde inte skapa delningslänk just nu – försök igen.');
      return;
    }

    const url = `${window.location.origin}${window.Shopify?.routes?.root || '/'}?wl=${encodeURIComponent(token)}`;
    const title = 'Min önskelista hos Trappbutiken';

    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch {
        /* användaren avbröt – fall igenom till kopiering */
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      this.note('Länken är kopierad – klistra in där du vill dela den.');
    } catch {
      this.note(url);
    }
  }

  note(text) {
    if (!this.noteEl) return;
    this.noteEl.textContent = text;
    this.noteEl.hidden = false;
    clearTimeout(this.noteTimer);
    this.noteTimer = setTimeout(() => {
      this.noteEl.hidden = true;
    }, 4000);
  }

  /* --- Badge i headern --- */
  syncBadge() {
    const badge = document.querySelector('[data-wubs-wishlist-count]');
    if (!badge) return;
    const n = WubsWishlistStore.count();
    badge.textContent = n;
    badge.hidden = n === 0;
  }
}

/* Kundmetafält-synk: läs serverns lista vid sidladdning. FÖRE elementet: lådans importFromUrl()
   skriver till listan, och den skrivningen ska se rätt ägare och hamna i synken (2026-11-03). */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', wubsWlBoot);
} else {
  wubsWlBoot();
}

if (!customElements.get('wubs-wishlist-drawer')) {
  customElements.define('wubs-wishlist-drawer', WubsWishlistDrawer);
}

/* Hjärtknappar var som helst på sajten: <button data-wubs-wishlist-toggle="handle"> */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-wubs-wishlist-toggle]');
  if (!btn) return;
  e.preventDefault();
  const on = WubsWishlistStore.toggle(btn.dataset.wubsWishlistToggle, btn.dataset.wubsWishlistVariant);
  btn.classList.toggle('is-active', on);
  btn.setAttribute('aria-pressed', String(on));
  // Lade till → öppna önskelistan (prompt om inloggning renderas där för gäst).
  if (on) document.querySelector('wubs-wishlist-drawer')?.open?.();
});

/* Markera sparade hjärtan vid sidladdning och när listan ändras */
const wubsWlSyncHearts = () => {
  document.querySelectorAll('[data-wubs-wishlist-toggle]').forEach((btn) => {
    const on = WubsWishlistStore.has(btn.dataset.wubsWishlistToggle);
    btn.classList.toggle('is-active', on);
    btn.setAttribute('aria-pressed', String(on));
  });
};
document.addEventListener(WUBS_WL_EVENT, wubsWlSyncHearts);
document.addEventListener('DOMContentLoaded', wubsWlSyncHearts);
